import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/contexts/ProfileContext";
import { useUserRole } from "@/hooks/useUserRole";

// Paths that must remain accessible even when the profile is incomplete.
const ALLOW_PREFIXES = [
  "/complete-profile",
  "/login",
  "/signup",
  "/vet-signup",
  "/auth",
  "/forgot-password",
  "/reset-password",
  "/mfa",
  "/unsubscribe",
  "/passport/share",
  "/co-owner",
  "/confirm-vet-customer",
];

/**
 * Global gate: any authenticated pet owner missing first or last name on
 * their profile is redirected to /complete-profile until they fill it in.
 * Vets and admins are skipped - they have their own onboarding paths.
 */
const RequireCompleteProfile = () => {
  const { user } = useAuth();
  const { profile, loading } = useProfile();
  const { role, loading: roleLoading } = useUserRole();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || loading || roleLoading) return;
    if (role === "admin" || role === "vet") return;
    const path = location.pathname;
    if (ALLOW_PREFIXES.some((p) => path === p || path.startsWith(p + "/"))) return;
    if (!profile.first_name || !profile.last_name) {
      navigate("/complete-profile", {
        replace: true,
        state: { from: location.pathname + location.search },
      });
    }
  }, [user, loading, roleLoading, role, profile.first_name, profile.last_name, location.pathname, location.search, navigate]);

  return null;
};

export default RequireCompleteProfile;
